import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { wizardProps, WizardPage } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useTranslator } from '@jutro/locale';
import { PremierConstants } from "premier-constant-service";
import { useUpdateSubmissionServices, validationUtil } from "premier-capability-hooks";
import AddlInterestTextBoxComponent from "../../components/AddlInterestTextBoxComponent/AddlInterestTextBoxComponent";
import styles from '../../SpaAndWellnessStyles.module.scss';
import presentationMetadata from './PropertyCoveragesPage.metadata.json5';
import messages from './PropertyCoveragesPage.messages';
import CoveragesPage from '../Coverages/CoveragesPage';

function PropertyCoveragesPage(props) {
    const {
        wizardData, updateWizardData, currentStep, callbackMap
    } = props;
    const { pageKey } = currentStep;
    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];
    const { initialValidation, onValidate } = useValidation('PropertyPage');
    const translator = useTranslator();
    const { lineName } = PremierConstants;
    const { updateSubmission } = useUpdateSubmissionServices();
    const getAccordianTitle = useCallback((index) => (
        <div className={styles.accordianTitle}>
            <p>
                {_.get(
                    wizardData,
                    `lobData.spaAndWellLine.coverables.locRisks.children[${index}].accountLocFie.displayName.value`
                )}
            </p>
            <p>
                {`${translator(messages.totalInsValue)}${_.get(wizardData.value, `lobData.spaAndWellLine.lineCoverages.locRiskCoverages.pswcoverages[${index}].totInsValFie`) === undefined ? 0 : 
                _.get( wizardData.value, `lobData.spaAndWellLine.lineCoverages.locRiskCoverages.pswcoverages[${index}].totInsValFie` )}`}
            </p>
        </div>
    ), [translator, wizardData]);


    const propertyCoveragesOverrides = useMemo(() => {
        const informationList = _.get(wizardData, 'lobData.spaAndWellLine.coverables.locRisks', []);
        let overrides = [];

        if (!_.isEmpty(informationList)) {
            overrides = informationList?.children.map((information, index) => ({
                [`locRiskCoverageComponent${index}`]: {
                    wizardData,
                    updateWizardData,
                    clausesBasePath: `lobData.spaAndWellLine.lineCoverages.locRiskCoverages.pswcoverages.children[${index}].coverages`,
                    title: messages.cov,
                    clasueType: 'coverages',
                    pathToGet: `spaAndWellLine.locRiskCoverages.pswcoverages[${index}].coverages`
                },
                [`additionalInterest${index}`]: {
                    wizardData,
                    updateWizardData,
                    addlIntPath: `lobData.spaAndWellLine.coverables.locRisks.children[${index}].additionalInterests`,
                    selectedOffering: _.get(wizardData, 'lobData.value.spaAndWellLine.selectedOffering_Ext'),
                    isLiaCov: false
                },
                [`propertyCoveragesAccordionDetails${index}`]: {
                    title: getAccordianTitle(index),
                    className: styles.gwOuterSpaAndWellnessAccordion
                },
                [`propertyCoveragesAccordion${index}`]: {
                    className: styles.gwAccordionSet
                }
            }));
        }

        return Object.assign({}, ...overrides);
    }, [getAccordianTitle, updateWizardData, wizardData]);

    const isOptionalCoverageVisible = () => {
        const propertyCovs = _.get(wizardData, 'lobData.spaAndWellLine.lineCoverages.propertyCoverages');
        return !_.isUndefined(propertyCovs) && propertyCovs.length > 0
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        optionalCoveragesPageTitle: {
            visible: isOptionalCoverageVisible()
        },
        optionalCoverageComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.spaAndWellLine.lineCoverages.propertyCoverages`,
            title: messages.cov,
            clasueType: 'coverages',
            pathToGet: `spaAndWellLine.propertyCoverages`,
            visible: isOptionalCoverageVisible()
        },
        ...propertyCoveragesOverrides,

    };

    const resolveCallbackMap = {
        ...callbackMap
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.content, wizardData, id, path, overrideProps),
        [metadata.content, wizardData, overrideProps]
    );

    const resolveComponentMap = {
        coveragespagecomponent: CoveragesPage,
        AddlInterestTextBoxComponent
    };

    const onNext = useCallback(async () => {
        const { productCode } = wizardData.value.baseData;
        const lobLineName = lineName[productCode];
    
        wizardData.value = await updateSubmission(
          lobLineName,
          "PropertyPage",
          wizardData
        );
    
        return wizardData;
      }, [lineName, updateSubmission, wizardData]);

      const onSave = useCallback(
            async () => {
                const subVMCopy = _.clone(wizardData)
                const productCode = _.get(subVMCopy.value, 'baseData.productCode');
                
                validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.spaAndWellLine.coverables.locRisks.value, ' ')
      
                const lobLineName = productCode ? lineName[productCode] : '';

                await updateSubmission(lobLineName, 'PropertyPage', subVMCopy);
                                
                return wizardData;
            },
            [lineName, wizardData, updateSubmission]
        );

    return (
        <WizardPage
            skipWhen={() => initialValidation}
            disableNext={!_.get(wizardData, 'lobData.spaAndWellLine.coverables.locRisks.aspects.subtreeValid')}
            onNext={onNext}
            onSave={onSave}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={wizardData}
                onModelChange={updateWizardData}
                callbackMap={resolveCallbackMap}
                resolveValue={readValue}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                componentMap={resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

PropertyCoveragesPage.propTypes = wizardProps;
export default withViewModelService(PropertyCoveragesPage);
